/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  category: [],
  branch: [],
  page: 1,
  perPage: 10,
};

const idsOf = (rows) => rows.map((item) => Number(item.id));

const filterValue = (ids, all) => {
  if (!all.length || (ids.length > 0 && ids.length === all.length)) return 'ALL';
  if (!ids.length) return 'NONE';
  return ids.join(',');
};

function RpProductStepPrice() {
  const mod = 'rp_product_step_price';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [urlExcel, setUrlExcel] = useState('');
  const [cboCategory, setCboCategory] = useState([]);
  const [cboBranch, setCboBranch] = useState([]);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const view = (data, lists) => {
    const categories = lists?.category || cboCategory;
    const branches = lists?.branch || cboBranch;
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      category: filterValue(data.category, categories),
      branch: filterValue(data.branch, branches),
      page: data.page,
      per_page: data.perPage,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('category', filterValue(find.category, cboCategory));
    params.append('branch', filterValue(find.branch, cboBranch));
    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        const categories = result.category || [];
        const branches = result.branch || [];
        setCboCategory(categories);
        setCboBranch(branches);
        setUrlExcel(result.excel);
        view(
          {
            ...find,
            category: idsOf(categories),
            branch: idsOf(branches),
          },
          { category: categories, branch: branches }
        );
      } else {
        Func.exitApp();
      }
    });
  };

  const multiSelect = (label, selected, options, key) => (
    <MDInput
      select
      fullWidth
      SelectProps={{
        multiple: true,
        renderValue: (picked) => {
          if (!picked.length || picked.length === options.length) return t('element.all');
          return options
            .filter((item) => picked.includes(Number(item.id)))
            .map((item) => item.name)
            .join(', ');
        },
      }}
      value={selected}
      onChange={(e) => {
        const { value } = e.target;
        if (value.includes('ALL')) {
          const allSelected = options.length > 0 && selected.length === options.length;
          view({ ...find, [key]: allSelected ? [] : idsOf(options), page: 1 });
        } else {
          view({ ...find, [key]: value.map(Number), page: 1 });
        }
      }}
      InputProps={{
        startAdornment: <InputAdornment position='start'>{label} :</InputAdornment>,
      }}
    >
      <MenuItem value='ALL'>
        <Checkbox checked={options.length > 0 && selected.length === options.length} />
        {t('element.all')}
      </MenuItem>
      {options.map((item) => (
        <MenuItem key={item.id} value={Number(item.id)}>
          <Checkbox checked={selected.includes(Number(item.id))} />
          {item.name}
        </MenuItem>
      ))}
    </MDInput>
  );

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_product`), t(`menu.rp_product_step_price`)]} />
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_product'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <MDBox
                    noValidate
                    component='form'
                    role='form'
                    onSubmit={(e) => {
                      e.preventDefault();
                      view({ ...find, page: 1 });
                    }}
                  >
                    <MDInput
                      name='searchform'
                      fullWidth
                      value={find.search}
                      type='search'
                      onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                        ),
                        autoComplete: 'off',
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  {multiSelect(t('element.category'), find.category, cboCategory, 'category')}
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  {multiSelect(t('attr.branch'), find.branch, cboBranch, 'branch')}
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={1}>
                  <MDBox display='flex' justifyContent='end' gap={1} onClick={excel}>
                    <MDButton variant='gradient' color='success'>
                      <Icon>table_view</Icon>&nbsp;Excel
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <TableContainer>
              <Table aria-label='step price report'>
                <TableHead>
                  <TableRow>
                    <TableCell width={70}>ลำดับ</TableCell>
                    <TableCell width={140}>รหัส</TableCell>
                    <TableCell>ชื่อสินค้า</TableCell>
                    <TableCell>{t('attr.branch')}</TableCell>
                    <TableCell align='right'>ราคาขาย</TableCell>
                    <TableCell>สเตปราคา</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={`${row.id}-${row.branch_id}`} hover>
                      <TableCell>{row.no}</TableCell>
                      <TableCell className='nowrap'>{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell className='nowrap'>{row.branch_name}</TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.sell_price)}
                      </TableCell>
                      <TableCell className='nowrap'>{row.step_price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </MDBox>
  );
}

export default RpProductStepPrice;
